"use client";

import { useEffect } from "react";

import {
  OPEN_LAYER_SELECTOR,
  backCloserOf,
  createBackCloser,
  lastInOrder,
  registeredLayers,
  subscribeBackClosers,
  type WatcherLike,
} from "@/lib/ui/back-closes";

/** Depois de um voltar, o tempo da animação de fechar das janelas antes de
 *  conferir de novo (e armar outro vigia, se ainda houver camada aberta). */
const RECHECK_MS = 300;

type CloseWatcherCtor = new () => WatcherLike;

/**
 * O voltar do Android fecha a janela aberta — o vigia único do app (regras e
 * motivo em `lib/ui/back-closes`). Montado uma vez na raiz: vale para o
 * escritório, o mestre e os portais. Sem UI.
 *
 * Só no aparelho de toque com `CloseWatcher` (Chrome do Android, PWA e TWA):
 * no computador o Esc já fecha, e o iPhone não tem voltar.
 */
export default function BackButtonCloses() {
  useEffect(() => {
    const Ctor = (window as unknown as { CloseWatcher?: CloseWatcherCtor })
      .CloseWatcher;
    if (typeof Ctor !== "function") return;
    if (!window.matchMedia("(pointer: coarse)").matches) return;

    const topLayer = () => {
      const layers = new Set<Element>(
        document.querySelectorAll(OPEN_LAYER_SELECTOR),
      );
      for (const layer of registeredLayers()) {
        if (layer instanceof Element && layer.isConnected) layers.add(layer);
      }
      return lastInOrder(
        layers,
        (a, b) =>
          (b.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_FOLLOWING) !==
          0,
      );
    };

    const closer = createBackCloser<Element>({
      topLayer,
      createWatcher: () => {
        try {
          return new Ctor();
        } catch {
          return null;
        }
      },
      close: (layer) => {
        const own = backCloserOf(layer);
        if (own) {
          own();
          return;
        }
        // O Esc que o Radix já trata: fecha a camada de cima com as regras
        // dela (confirmação de descarte, bloqueio enquanto salva).
        layer.dispatchEvent(
          new KeyboardEvent("keydown", {
            key: "Escape",
            code: "Escape",
            keyCode: 27,
            bubbles: true,
            cancelable: true,
          }),
        );
      },
      defer: (fn) => {
        window.setTimeout(fn, RECHECK_MS);
      },
    });

    // As camadas do Radix entram e saem como filhas diretas do <body>: basta
    // olhar essa lista, sem varrer a página inteira a cada mudança.
    const observer = new MutationObserver(() => closer.sync());
    observer.observe(document.body, { childList: true });
    const unsubscribe = subscribeBackClosers(() => closer.sync());
    closer.sync();

    return () => {
      observer.disconnect();
      unsubscribe();
      closer.dispose();
    };
  }, []);

  return null;
}
